import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useNavigate, useBeforeUnload } from 'react-router-dom';
import NavigationBlocker, { NavigationBlockerRef } from '../Navigation/NavigationBlocker';
import { useNavigation } from '../../contexts/NavigationContext';
import { getApiUrl } from '../../config';
import { kitchenClientHeaders } from '../../services/clientContext';
import { useAuth } from '../../contexts/AuthContext';
import { useRecipeUndo } from '../../contexts/RecipeUndoContext';
import { recipeService, RecipeSidebarRef, RecipeSidebarUpdate } from './RecipeSidebar'; // Import shared service
import { Recipe, RecipeTriggerType } from '../../types/recipe';
import MarkupEditor from './MarkupEditor';
import { useAppShell } from '../../contexts/AppShellContext';
import StreamingSaltShaker from '../StreamingSaltShaker';
import { PlanLimitDialog } from '../Billing/PlanLimitPrompt';
import { BillingLimit, billingLimitFromData } from '../../utils/billingLimit';
import { getMyBilling } from '../../services/billingService';
import RecipeExecutionGuide, { RecipeExecutionGuideStatus } from './RecipeExecutionGuide';
import AlertDialog from '../Common/AlertDialog';

interface RecipeEditorProps {
  initialRecipe: Recipe;
  onSave?: (content: string, name: string) => Promise<void>;
  onUnsavedChangesChange?: (hasUnsavedChanges: boolean) => void;
  onLastSaveTimeChange?: (lastSaveTime: Date | null) => void;
  onStatusChange?: (recipe: RecipeSidebarUpdate) => void;
  onChefAttention?: (focusInput?: boolean) => void;
  onOpenLiveOutput?: (focusOutput?: boolean) => void;
  chefPanelVisible?: boolean;
  onToggleChefPanel?: () => void;
  sidebarRef?: React.RefObject<RecipeSidebarRef>;
  recipeName?: string;
  triggerType: RecipeTriggerType;
  generatedContent?: string;
  generationActive?: boolean;
  generationStatus?: string | null;
  generationError?: string | null;
  onRetryGeneration?: () => Promise<void>;
  onContentChange?: (content: string) => void;
}

const AUTO_SAVE_DELAY_MS = 3000;

const formatRunFrequency = (minutes: number | null | undefined) => {
  if (minutes === null) return 'Not included';
  if (minutes === undefined) return '—';
  return `Every ${minutes.toLocaleString()} min`;
};

const RecipeFireAnimation = () => (
  <div className="recipe-fire" aria-hidden="true">
    <div className="recipe-fire__glow" />
    <svg className="recipe-fire__svg" viewBox="0 0 180 210" role="presentation">
      <defs>
        <linearGradient id="recipe-fire-outer" x1="0" y1="1" x2="0.6" y2="0">
          <stop offset="0" stopColor="#dc2626" />
          <stop offset="0.48" stopColor="#f97316" />
          <stop offset="1" stopColor="#fb923c" />
        </linearGradient>
        <linearGradient id="recipe-fire-middle" x1="0.2" y1="1" x2="0.7" y2="0">
          <stop offset="0" stopColor="#f97316" />
          <stop offset="0.55" stopColor="#facc15" />
          <stop offset="1" stopColor="#fde68a" />
        </linearGradient>
        <linearGradient id="recipe-fire-core" x1="0.5" y1="1" x2="0.5" y2="0">
          <stop offset="0" stopColor="#fff7ed" />
          <stop offset="0.7" stopColor="#fef08a" />
          <stop offset="1" stopColor="#fbbf24" />
        </linearGradient>
        <filter id="recipe-fire-soft-glow" x="-60%" y="-60%" width="220%" height="240%">
          <feGaussianBlur stdDeviation="5" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      <g className="recipe-fire__embers" fill="#fbbf24">
        <circle cx="56" cy="69" r="3" />
        <circle cx="122" cy="82" r="2.5" />
        <circle cx="83" cy="43" r="2" />
        <circle cx="105" cy="54" r="1.8" />
      </g>
      <g filter="url(#recipe-fire-soft-glow)">
        <path
          className="recipe-fire__outer"
          fill="url(#recipe-fire-outer)"
          d="M91 194c-38 0-66-25-66-62 0-30 17-48 34-70 10-13 16-28 18-46 24 17 39 38 37 63 10-7 17-17 20-30 15 19 23 42 21 65-2 47-28 80-64 80Z"
        />
        <path
          className="recipe-fire__middle"
          fill="url(#recipe-fire-middle)"
          d="M91 191c-25 0-44-18-44-44 0-20 12-33 24-48 8-10 13-21 14-34 18 13 28 29 26 46 8-5 13-12 16-21 9 14 13 29 11 44-3 34-21 57-47 57Z"
        />
        <path
          className="recipe-fire__core"
          fill="url(#recipe-fire-core)"
          d="M91 190c-14 0-25-11-25-27 0-12 7-20 14-29 5-6 8-13 9-21 11 8 17 18 15 28 5-3 8-7 10-13 5 9 7 18 5 27-3 21-13 35-28 35Z"
        />
      </g>
    </svg>
  </div>
);

async function readJsonResponse(response: Response): Promise<any> {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw new Error(`Server returned an unexpected response (${response.status}). Please try again.`);
  }
  return response.json();
}

const RecipeEditor: React.FC<RecipeEditorProps> = ({ initialRecipe, onSave, onUnsavedChangesChange, onLastSaveTimeChange, onStatusChange, onChefAttention, onOpenLiveOutput, chefPanelVisible = false, onToggleChefPanel, sidebarRef, recipeName, triggerType, generatedContent, generationActive = false, generationStatus, generationError, onRetryGeneration, onContentChange }) => {
  const [recipeId, setRecipeId] = useState<number | null>(initialRecipe.id);
  const [content, setContent] = useState(() => (initialRecipe.draft_content ?? initialRecipe.content).replace(/\n{20,}/g, '\n\n'));
  const [liveContent, setLiveContent] = useState(() => initialRecipe.content.replace(/\n{20,}/g, '\n\n'));
  const [, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isCooking, setIsCooking] = useState(false);
  const [isCheckingStart, setIsCheckingStart] = useState(false);
  const [isRunning, setIsRunning] = useState(false); // For the Run button process
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [planLimit, setPlanLimit] = useState<BillingLimit | null>(null);
  const [billingSummary, setBillingSummary] = useState<any>(null);
  const [resetError, setResetError] = useState<string | null>(null);
  const [alertDialog, setAlertDialog] = useState<{ title: string; message: string } | null>(null);
  const [isActive, setIsActive] = useState(initialRecipe.is_active);
  const [planSuspendedAt, setPlanSuspendedAt] = useState<string | null>(initialRecipe.planSuspendedAt || null);
  const [liveTriggerType, setLiveTriggerType] = useState<RecipeTriggerType>(initialRecipe.trigger_type || 'manual');
  const [executionGuide, setExecutionGuide] = useState<{
    status: RecipeExecutionGuideStatus;
    triggerType: RecipeTriggerType | null;
    executionId: string | null;
    nextRunAt: string | null;
    errorMessage: string | null;
    minimized: boolean;
  } | null>(null);
  const [editorFocusRequest, setEditorFocusRequest] = useState(0);
  const controlsColumnRef = useRef<HTMLDivElement>(null);
  const [hiddenEditorHeight, setHiddenEditorHeight] = useState<number | null>(null);

  // Run‑flow progress phase: idle → autosave → cook → activate → deploy → validate
// Added 'stop' so the same overlay is used when a recipe is being
// deactivated via the Stop button.
type RunPhase =
  | 'idle'
  | 'autosave'
  | 'start'
  | 'apply'
  | 'stop';
  const [runPhase, setRunPhase] = useState<RunPhase>('idle');
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(
    initialRecipe.last_save_time ? new Date(initialRecipe.last_save_time) : null
  );
  const [, setLastActivationTime] = useState<Date | null>(
    initialRecipe.last_activation_time ? new Date(initialRecipe.last_activation_time) : null
  );
  const [hasStarted, setHasStarted] = useState(Boolean(initialRecipe.last_activation_time || initialRecipe.last_execution_at));
  const hasUnappliedChanges = content !== liveContent
    || triggerType !== liveTriggerType;

  useEffect(() => {
    onLastSaveTimeChange?.(lastSaveTime);
  }, [lastSaveTime, onLastSaveTimeChange]);
  const navigate = useNavigate();
  const navigationRef = useRef<NavigationBlockerRef>(null);
  const startRequestInFlightRef = useRef(false);
  const { setHasUnsavedChanges: setGlobalUnsavedChanges } = useNavigation();
  const saveTimeoutRef = useRef<NodeJS.Timeout>();
  const { token } = useAuth();
  const { showRecipeUndo } = useRecipeUndo();
  const { skin } = useAppShell();
  const isYolo = skin === 'yolo';
  const effectiveName = recipeName || '';
  const showGenerationAnimation = generationActive && !generatedContent?.trim();

  useLayoutEffect(() => {
    if (!isYolo || chefPanelVisible) {
      setHiddenEditorHeight(null);
      return;
    }

    const controlsColumn = controlsColumnRef.current;
    if (!controlsColumn) return;
    const desktopLayout = window.matchMedia('(min-width: 1024px)');
    let observer: ResizeObserver | null = null;

    const matchEditorToControls = () => {
      const nextHeight = Math.ceil(controlsColumn.getBoundingClientRect().height);
      setHiddenEditorHeight(current => current === nextHeight ? current : nextHeight);
    };

    const syncDesktopSizing = () => {
      if (!desktopLayout.matches) {
        observer?.disconnect();
        observer = null;
        setHiddenEditorHeight(null);
        return;
      }
      matchEditorToControls();
      if (!observer) {
        observer = new ResizeObserver(matchEditorToControls);
        observer.observe(controlsColumn);
      }
    };

    syncDesktopSizing();
    desktopLayout.addEventListener('change', syncDesktopSizing);
    return () => {
      desktopLayout.removeEventListener('change', syncDesktopSizing);
      observer?.disconnect();
    };
  }, [chefPanelVisible, isYolo]);

  useEffect(() => {
    if (!token) return;
    let active = true;

    const refreshBillingSummary = () => {
      void getMyBilling()
        .then(summary => {
          if (active) setBillingSummary(summary);
        })
        .catch(error => console.warn('Could not load subscription summary:', error));
    };

    refreshBillingSummary();
    document.addEventListener('billingComplianceChanged', refreshBillingSummary);
    return () => {
      active = false;
      document.removeEventListener('billingComplianceChanged', refreshBillingSummary);
    };
  }, [token]);

  // Notify parent component and global context of unsaved changes
  useEffect(() => {
    if (onUnsavedChangesChange) {
      onUnsavedChangesChange(hasUnsavedChanges);
    }
    setGlobalUnsavedChanges(hasUnsavedChanges);
  }, [hasUnsavedChanges, onUnsavedChangesChange, setGlobalUnsavedChanges]);

  useEffect(() => {
    setExecutionGuide(null);
  }, [initialRecipe.id]);

  useEffect(() => {
    if (!executionGuide?.executionId || !token || executionGuide.triggerType !== 'manual') return;
    if (['finished', 'failed', 'needs_user'].includes(executionGuide.status)) return;
    let stopped = false;

    const refreshExecutionStatus = async () => {
      try {
        const query = new URLSearchParams({ executionPage: '1', occurrencePage: '1' });
        const response = await fetch(getApiUrl(`recipes/${recipeId}/activity?${query.toString()}`), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) return;
        const activity = await response.json();
        const execution = activity.executions?.find((item: { id: string }) => item.id === executionGuide.executionId);
        if (!execution || stopped) return;

        const failedStatuses = ['rejected', 'failed', 'ambiguous_after_write', 'cancelled', 'timed_out'];
        const finishedStatuses = ['succeeded', 'partial'];
        const nextStatus: RecipeExecutionGuideStatus = execution.status === 'running'
          ? 'running'
          : execution.status === 'needs_user'
            ? 'needs_user'
            : failedStatuses.includes(execution.status)
              ? 'failed'
              : finishedStatuses.includes(execution.status)
                ? 'finished'
                : 'queued';
        setExecutionGuide(current => {
          if (!current || current.executionId !== execution.id) return current;
          return { ...current, status: nextStatus, errorMessage: nextStatus === 'failed' ? execution.message : null };
        });
      } catch {
        // Live output remains available if this lightweight status refresh misses a cycle.
      }
    };

    void refreshExecutionStatus();
    const interval = window.setInterval(() => void refreshExecutionStatus(), 2_500);
    return () => {
      stopped = true;
      window.clearInterval(interval);
    };
  }, [executionGuide?.executionId, executionGuide?.status, executionGuide?.triggerType, recipeId, token]);

  // Handle browser navigation/refresh/close
  useBeforeUnload(
    useCallback(
      (event) => {
        if (hasUnsavedChanges) {
          event.preventDefault();
          event.returnValue = '';
        }
      },
      [hasUnsavedChanges]
    )
  );

  const loadRecipe = useCallback(async (recipeId: string) => {
    try {
      // Always use the ID directly
      const endpoint = `recipes/${recipeId}`;
      
      const response = await fetch(getApiUrl(endpoint), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load recipe');
      }

      const data = await response.json();
      // A previous contentEditable implementation could multiply layout
      // newlines after every keystroke. Keep affected recipes renderable while
      // preserving ordinary intentional spacing. The repaired value is saved
      // the next time the user edits.
      const loadedContent = (data.draft_content ?? data.content).replace(/\n{20,}/g, '\n\n');
      
      // Loading server content does not count as a local, unsaved edit.
      setContent(loadedContent);
      setLiveContent(data.content.replace(/\n{20,}/g, '\n\n'));
      
      // Save the ID for future use. Existing recipe names are owned by
      // RecipeEdit and passed in as props so renames cannot leave this
      // editor with a stale hidden copy.
      setRecipeId(data.id);
      
      setIsActive(data.is_active);
      setPlanSuspendedAt(data.planSuspendedAt || null);
      setLiveTriggerType(data.trigger_type || 'manual');
      
      // Set timestamp data if available
      if (data.last_save_time) setLastSaveTime(new Date(data.last_save_time));
      if (data.last_activation_time) setLastActivationTime(new Date(data.last_activation_time));
      setHasStarted(Boolean(data.last_activation_time || data.last_execution_at));
    } catch (err) {
      navigate('/');
    }
  }, [navigate, token]);

  useEffect(() => {
    if (!generationActive || generatedContent === undefined) return;
    setContent(generatedContent);
    setHasUnsavedChanges(false);
    setGlobalUnsavedChanges(false);
  }, [generatedContent, generationActive, setGlobalUnsavedChanges]);

  // Add event listener for refreshRecipeContent events
  useEffect(() => {
    const handleRefreshEvent = (event: CustomEvent) => {
      // Check if this event is for this recipe, either by ID or name
      if (
        (event.detail?.recipeId && (
          event.detail?.recipeId === recipeId || // Match by ID (preferred)
          event.detail?.recipeId === effectiveName // Fallback to match by name
        ))
      ) {
        console.log('Refreshing recipe content for:', effectiveName, '(ID:', recipeId, ')');
        // Chef commits approved edits as the new saved recipe. Cancel any
        // pending autosave from the pre-Chef editor state before loading it,
        // otherwise that stale text can immediately recreate an old draft.
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        setHasUnsavedChanges(false);
        setGlobalUnsavedChanges(false);
        // Reload the recipe content using ID if available
        if (recipeId) {
          loadRecipe(String(recipeId));
        } else if (effectiveName) {
          loadRecipe(effectiveName);
        }
      }
    };

    document.addEventListener('refreshRecipeContent', handleRefreshEvent as EventListener);
    return () => document.removeEventListener('refreshRecipeContent', handleRefreshEvent as EventListener);
  }, [effectiveName, loadRecipe, recipeId, setGlobalUnsavedChanges]);

  const autoSave = useCallback(async () => {
    if (generationActive) return;
    if (!effectiveName.trim() || !content.trim()) return;
    
    setIsSaving(true);
    try {
      if (onSave) {
        await onSave(content, effectiveName);
        // When using onSave prop, we don't get timestamp data returned
        // so we'll update it ourselves
        setLastSaveTime(new Date());
      } else {
        const pathParts = window.location.pathname.split('/');
        const recipeId = pathParts[pathParts.length - 2];
        const response = await fetch(getApiUrl(`recipes/${recipeId}`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ content })
        });

        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.message || 'Failed to save recipe');
        }
        
        // Update timestamps from the response data
        if (data && data.last_save_time) {
          // Make sure we update with the server time
          setLastSaveTime(new Date(data.last_save_time));
        } else {
          // If the API doesn't return a timestamp, set it to now
          const now = new Date();
          setLastSaveTime(now);
        }
      }
      setHasUnsavedChanges(false);
      setGlobalUnsavedChanges(false);
    } catch (err) {
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [effectiveName, content, generationActive, onSave, setGlobalUnsavedChanges, token]);

  // Debounced auto-save effect
  useEffect(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    if (hasUnsavedChanges) {
      saveTimeoutRef.current = setTimeout(() => {
        void autoSave().catch(error => console.error('Could not save recipe:', error));
      }, AUTO_SAVE_DELAY_MS);
    }

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [hasUnsavedChanges, autoSave]);

  const handleContentChange = (nextContent: string) => {
    if (generationActive) return;
    setContent(nextContent);
    onContentChange?.(nextContent);
    setHasUnsavedChanges(true);
  };

  const handleStartRecipe = async () => {
    if (startRequestInFlightRef.current) return;
    const showStartGuide = !isActive && !planSuspendedAt;
    startRequestInFlightRef.current = true;
    setIsCheckingStart(true);
    if (showStartGuide) {
      setExecutionGuide({
        status: 'checking',
        triggerType: null,
        executionId: null,
        nextRunAt: null,
        errorMessage: null,
        minimized: false,
      });
    }
    try {
      if (hasUnsavedChanges) {
        setRunPhase('autosave');
        await autoSave();
        setRunPhase('idle');
      }
      if (!recipeId) throw new Error('This recipe is not ready yet');
      if (planSuspendedAt) {
        setIsCheckingStart(false);
        setIsRunning(true);
        setRunPhase('start');
        const response = await fetch(getApiUrl(`recipes/${recipeId}/resume-plan-suspension`), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        const data = await readJsonResponse(response);
        if (!response.ok) {
          const nextPlanLimit = billingLimitFromData(data);
          if (nextPlanLimit) {
            setPlanLimit(nextPlanLimit);
            return;
          }
          if (data.details?.recipeStopped === true) {
            setIsActive(false);
            onStatusChange?.({ id: recipeId, is_active: false });
          }
          if (data.code === 'webhook_setup_required') document.dispatchEvent(new CustomEvent('refreshConversations'));
          throw new Error(data.message || 'Could not resume this recipe');
        }
        setPlanSuspendedAt(null);
        setIsActive(Boolean(data.is_active));
        onStatusChange?.({ ...data, id: recipeId, planSuspendedAt: null });
        document.dispatchEvent(new CustomEvent('refreshChefMutation', { detail: { recipeId } }));
        document.dispatchEvent(new CustomEvent('billingComplianceChanged'));
        setSuccessToastVisible(true);
        setSuccessProgress(100);
        return;
      }
      const action = isActive ? 'apply' : 'start';

      setIsCheckingStart(false);
      setIsRunning(true);
      setRunPhase(isActive ? 'apply' : 'start');
      const response = await fetch(getApiUrl(`recipes/${recipeId}/${action}`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...kitchenClientHeaders(),
        },
      });
      const data = await readJsonResponse(response);
      if (!response.ok) {
        const nextPlanLimit = billingLimitFromData(data);
        if (nextPlanLimit) {
          setPlanLimit(nextPlanLimit);
          if (nextPlanLimit.details?.recipeStopped === true) {
            setIsActive(false);
            onStatusChange?.({ id: recipeId, is_active: false });
          }
          if (showStartGuide) setExecutionGuide(null);
          return;
        }
      }
      if (!response.ok) {
        if (data.details?.recipeStopped === true) {
          setIsActive(false);
          onStatusChange?.({ id: recipeId, is_active: false });
        }
        if (data.code === 'webhook_setup_required') document.dispatchEvent(new CustomEvent('refreshConversations'));
        throw new Error(data.message || 'Could not start recipe');
      }
      setIsActive(Boolean(data.recipe?.is_active));
      setPlanSuspendedAt(data.recipe?.planSuspendedAt || null);
      if (data.recipe) {
        setContent(data.recipe.content);
        setLiveContent(data.recipe.content);
        setLiveTriggerType(data.recipe.trigger_type);
        setHasStarted(true);
      }
      if (showStartGuide && data.recipe) {
        const classifiedTrigger = data.recipe.trigger_type as RecipeTriggerType;
        const executionStatus = data.execution?.status as string | undefined;
        const guideStatus: RecipeExecutionGuideStatus = classifiedTrigger === 'event'
          ? 'webhook_ready'
          : classifiedTrigger === 'schedule'
            ? data.scheduleStatus === 'needs_user' ? 'needs_user' : 'scheduled'
            : executionStatus === 'running' ? 'running' : 'queued';
        setExecutionGuide(current => current ? {
          ...current,
          status: guideStatus,
          triggerType: classifiedTrigger,
          executionId: data.execution?.id || null,
          nextRunAt: data.schedule?.nextOccurrenceAt || null,
        } : current);
      }
      if (data.scheduleStatus === 'needs_user') {
        document.dispatchEvent(new CustomEvent('refreshConversations'));
        if (data.recipe?.id) onStatusChange?.(data.recipe);
        return;
      }
      if (data.recipe?.last_activation_time) {
        setLastActivationTime(new Date(data.recipe.last_activation_time));
      }
      if (data.recipe?.id) onStatusChange?.(data.recipe);
      document.dispatchEvent(new CustomEvent('billingComplianceChanged'));
      document.dispatchEvent(new CustomEvent('refreshChefMutation', { detail: { recipeId } }));
      setSuccessToastVisible(!isActive && !showStartGuide);
      setSuccessProgress(100);
      if (successToastTimeoutRef.current) clearTimeout(successToastTimeoutRef.current);
      const startedAt = Date.now();
      const intervalId = window.setInterval(() => {
        const progress = Math.max(0, 1 - (Date.now() - startedAt) / 5000) * 100;
        setSuccessProgress(progress);
        if (progress <= 0) {
          clearInterval(intervalId);
          setSuccessToastVisible(false);
        }
      }, 100);
      successToastTimeoutRef.current = intervalId as unknown as NodeJS.Timeout;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Could not start recipe';
      if (showStartGuide) {
        setExecutionGuide(current => current ? { ...current, status: 'failed', errorMessage } : current);
      } else {
        setAlertDialog({ title: 'Could not start recipe', message: errorMessage });
      }
    } finally {
      startRequestInFlightRef.current = false;
      setIsCheckingStart(false);
      setIsRunning(false);
      setRunPhase('idle');
    }
  };

  const handleDiscardChanges = async () => {
    if (!recipeId || !hasUnappliedChanges) return;
    try {
      setIsCooking(true);
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      setHasUnsavedChanges(false);
      setGlobalUnsavedChanges(false);
      const response = await fetch(getApiUrl(`recipes/${recipeId}/discard-changes`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await readJsonResponse(response);
      if (!response.ok) throw new Error(data.message || 'Could not discard changes');
      setContent(data.recipe.content);
      setLiveContent(data.recipe.content);
      setLiveTriggerType(data.recipe.trigger_type);
      setHasUnsavedChanges(false);
      setGlobalUnsavedChanges(false);
      if (data.recipe.last_save_time) setLastSaveTime(new Date(data.recipe.last_save_time));
      document.dispatchEvent(new CustomEvent('refreshChefMutation', { detail: { recipeId } }));
    } catch (error) {
      setAlertDialog({
        title: 'Could not discard changes',
        message: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setIsCooking(false);
    }
  };

  // Helper to refetch recipe row and sync local state
  async function refreshRecipeStatus() {
    if (!recipeId) return;
    try {
      const p = await fetchRecipeRow();
      if (!p) return;
      setIsActive(p.is_active);
      setPlanSuspendedAt(p.planSuspendedAt || null);
      setLiveTriggerType(p.trigger_type || 'manual');
      if (p.last_activation_time) setLastActivationTime(new Date(p.last_activation_time));
      setHasStarted(Boolean(p.last_activation_time || p.last_execution_at));
      if (p.last_save_time) setLastSaveTime(new Date(p.last_save_time));
    } catch (e) {
      console.error('Failed to refresh recipe status:', e);
    }
  }

  // Runtime state can change without any browser action (for example a
  // one-shot recipe exits normally). Keep the controls synchronized with
  // the backend's observed process state.
  useEffect(() => {
    if (!recipeId) return;

    const statusRefresh = window.setInterval(refreshRecipeStatus, 5_000);
    return () => window.clearInterval(statusRefresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipeId]);

  // Utility to fetch the recipe row
  async function fetchRecipeRow() {
    try {
      // Make sure we have a valid recipe ID
      if (!recipeId) {
        console.error('Invalid recipe ID for fetching row');
        return null;
      }

      const res = await fetch(getApiUrl(`recipes/${recipeId}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      console.error('Failed to fetch recipe row:', e);
      return null;
    }
  }

  const handleToggleActivation = async () => {
    try {
      if (!isActive) return;
      setRunPhase('stop');

      // Also flag component as busy to disable buttons
      setIsCooking(true);
      
      // Make sure we have a valid recipe ID
      if (!recipeId) {
        throw new Error('Invalid recipe ID for toggling activation');
      }

      const response = await fetch(getApiUrl(`recipes/${recipeId}/stop`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to toggle activation');
      }

      // Only update the state after successful response and use the state from the backend
      setIsActive(Boolean(data.recipe?.is_active));
      setPlanSuspendedAt(null);
      
      // Update timestamps based on the response
      if (data.recipe?.last_activation_time) {
        setLastActivationTime(new Date(data.recipe.last_activation_time));
      }
      
      // Notify parent component that status has changed
      if (data.recipe?.id) onStatusChange?.(data.recipe);
      document.dispatchEvent(new CustomEvent('billingComplianceChanged'));
    } catch (error: unknown) {
      console.error('Error toggling activation:', error);
      // Show error to user with proper type handling
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setAlertDialog({ title: 'Could not stop recipe', message: errorMessage });
    } finally {
      setIsCooking(false);
      setRunPhase('idle');
    }
  };

  // Reset the entire recipe environment (files + database state)
  const handleReset = async () => {
    if (!effectiveName) return;

    try {
      setIsRunning(true);
      // Make sure we have a valid recipe ID
      if (!recipeId) {
        throw new Error('Invalid recipe ID for reset');
      }

      // Reset clears runtime history, not editor changes. Flush a pending
      // debounced save first so the backend can make the latest draft the new
      // clean recipe state.
      if (hasUnsavedChanges) {
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        await autoSave();
      }

      const resp = await fetch(getApiUrl(`recipes/${recipeId}/reset`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await resp.json();

      if (!resp.ok) {
        const msg = data?.message || 'Failed to reset recipe';
        throw new Error(msg);
      }

      // Notify any ChatBox components to refresh conversations list so the
      // soft‑deleted ones disappear immediately.
      const refreshEvent = new CustomEvent('refreshConversations', {
        detail: { 
          recipeId: recipeId, // Use numeric ID (preferred)
          recipeName: effectiveName // Include name for backward compatibility
        }
      });
      document.dispatchEvent(refreshEvent);

      // Reset local state so UI reflects backend changes
      setIsActive(false);
      setHasStarted(false);
      setContent(data.recipe.content);
      setLiveContent(data.recipe.content);
      setLiveTriggerType(data.recipe.trigger_type);
      setLastActivationTime(null);
      setLastSaveTime(data.last_save_time ? new Date(data.last_save_time) : new Date());
      setHasUnsavedChanges(false);

      // Notify parent if needed
      onStatusChange?.({ id: recipeId, is_active: false });
      document.dispatchEvent(new CustomEvent('refreshChefMutation', { detail: { recipeId } }));
      setShowResetDialog(false);
    } catch (err) {
      console.error('Reset failed:', err);
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setResetError(`Failed to reset recipe: ${msg}`);
    } finally {
      setIsRunning(false);
    }
  };

  // State for followup toast
  const [followupToastVisible, setFollowupToastVisible] = useState(false);
  const followupToastTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [followupProgress] = useState(100);
  
  // State for success toast
  const [successToastVisible, setSuccessToastVisible] = useState(false);
  const successToastTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [successProgress, setSuccessProgress] = useState(100);
  
  // Cleanup on unmount
  useEffect(() => {
    const followupToastTimer = followupToastTimeoutRef;
    const successToastTimer = successToastTimeoutRef;
    return () => {
      if (followupToastTimer.current) {
        clearTimeout(followupToastTimer.current);
      }
      if (successToastTimer.current) {
        clearTimeout(successToastTimer.current);
      }
    };
  }, []);
  
  const handleDelete = async () => {
    let recipeId = 0;
    try {
      // Get the recipe ID from the URL
      // The URL format is /recipes/:id/edit
      const pathParts = window.location.pathname.split('/');
      recipeId = parseInt(pathParts[pathParts.length - 2], 10); // Get the ID before 'edit'
      
      // Check if ID is defined and valid
      if (isNaN(recipeId) || !token) {
        throw new Error('Recipe ID or authentication token is missing');
      }

      await recipeService.deleteRecipe(recipeId, token);
      document.dispatchEvent(new CustomEvent('billingComplianceChanged'));
      if (sidebarRef && sidebarRef.current) {
        sidebarRef.current.handleDeletedRecipe(recipeId);
      }
      navigate('/recipes/new', { replace: true });
      showRecipeUndo({
        recipeId,
        recipeName: effectiveName,
        reopenOnUndo: true,
      });
    } catch (err: unknown) {
      console.error('Error deleting recipe:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setAlertDialog({ title: 'Could not delete recipe', message: errorMessage });
    }
  };

  const hasInstructions = Boolean(content.trim());
  const primaryActionLabel = planSuspendedAt
    ? 'Resume recipe'
    : isActive
    ? 'Apply changes'
    : hasStarted && hasUnappliedChanges
      ? 'Start with changes'
      : 'Start recipe';
  const primaryActionDisabled = generationActive
    || isCheckingStart
    || isRunning
    || isCooking
    || !effectiveName
    || !hasInstructions
    || !recipeId
    || (isActive && !planSuspendedAt && !hasUnappliedChanges);
  const recipeStateTitle = planSuspendedAt
    ? 'Paused by plan'
    : isActive
    ? hasUnappliedChanges ? 'Current version still active' : 'Recipe is active'
    : hasStarted
      ? hasUnappliedChanges ? 'Stopped with saved changes' : 'Recipe is stopped'
      : 'Not started';
  const automaticRecipesUsed = billingSummary?.compliance?.automaticRecipes?.running
    ?? billingSummary?.usage?.activeRecipes
    ?? 0;
  const automaticRecipesIncluded = billingSummary?.plan?.maxActiveRecipes;
  const automaticRecipesUsagePercent = typeof automaticRecipesIncluded === 'number' && automaticRecipesIncluded > 0
    ? Math.min(100, Math.round((automaticRecipesUsed / automaticRecipesIncluded) * 100))
    : 0;
  const monthlyRuntimeSeconds = billingSummary?.plan?.monthlyRuntimeSeconds;
  const monthlyTimeUsedMinutes = Math.ceil((billingSummary?.usage?.runtimeSeconds ?? 0) / 60);
  const monthlyTimeIncludedMinutes = typeof monthlyRuntimeSeconds === 'number'
    ? Math.floor(monthlyRuntimeSeconds / 60)
    : null;
  const monthlyTimeUsagePercent = typeof monthlyRuntimeSeconds === 'number' && monthlyRuntimeSeconds > 0
    ? Math.min(100, Math.round(((billingSummary?.usage?.runtimeSeconds ?? 0) / monthlyRuntimeSeconds) * 100))
    : 0;

  return (
    <>
      {planLimit && <PlanLimitDialog limit={planLimit} onClose={() => setPlanLimit(null)} />}
      {alertDialog && (
        <AlertDialog
          title={alertDialog.title}
          message={alertDialog.message}
          onClose={() => setAlertDialog(null)}
        />
      )}
      {executionGuide && (
        <RecipeExecutionGuide
          status={executionGuide.status}
          triggerType={executionGuide.triggerType}
          nextRunAt={executionGuide.nextRunAt}
          errorMessage={executionGuide.errorMessage}
          minimized={executionGuide.minimized}
          onMinimize={() => setExecutionGuide(current => current ? { ...current, minimized: true } : current)}
          onClose={() => setExecutionGuide(null)}
          onRestore={() => setExecutionGuide(current => current ? { ...current, minimized: false } : current)}
          onAskChef={() => {
            onChefAttention?.(true);
          }}
          onViewLiveOutput={() => {
            onOpenLiveOutput?.(true);
          }}
          onKeepEditing={() => {
            setEditorFocusRequest(current => current + 1);
          }}
        />
      )}
      {(showResetDialog || resetError) && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="reset-dialog-title"
          onClick={() => {
            if (!isRunning) {
              setShowResetDialog(false);
              setResetError(null);
            }
          }}
        >
          <div
            className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5"
            onClick={event => event.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M12 9v4m0 4h.01M10.3 3.8 2.7 17a2 2 0 0 0 1.7 3h15.2a2 2 0 0 0 1.7-3L13.7 3.8a2 2 0 0 0-3.4 0Z" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <h2 id="reset-dialog-title" className="text-lg font-semibold text-gray-900">
                    {resetError ? 'Reset failed' : 'Reset this recipe?'}
                  </h2>
                  <p className={`mt-2 text-sm leading-6 ${resetError ? 'text-red-700' : 'text-gray-600'}`}>
                    {resetError || 'This deletes every version, log, and state file for this recipe, then deactivates it. This action cannot be undone.'}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-100 bg-gray-50 px-6 py-4">
              {resetError ? (
                <button
                  type="button"
                  onClick={() => {
                    setResetError(null);
                    setShowResetDialog(false);
                  }}
                  className="rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-black"
                >
                  Close
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    disabled={isRunning}
                    onClick={() => setShowResetDialog(false)}
                    className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-100 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={isRunning}
                    onClick={() => void handleReset()}
                    className="rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-wait disabled:opacity-60"
                  >
                    {isRunning ? 'Resetting…' : 'Reset recipe'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Run‑phase overlay */}
      {runPhase !== 'idle' && !isYolo && !executionGuide && (
        <div className="fixed inset-0 bg-black/40 flex flex-col items-center justify-center z-[9998] pointer-events-none">
          <div className="flex items-center space-x-3 bg-white rounded-lg px-6 py-4 shadow-xl animate-pulse">
            <svg className="h-6 w-6 text-indigo-600 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
            <span className="text-lg font-medium text-gray-800 capitalize">
              {runPhase === 'autosave' && 'Saving'}
              {runPhase === 'start' && 'Starting'}
              {runPhase === 'apply' && 'Applying'}
              {runPhase === 'stop' && 'Stopping'}
              …
            </span>
          </div>
        </div>
      )}
      <NavigationBlocker
        hasUnsavedChanges={hasUnsavedChanges}
        onConfirm={() => {
          setHasUnsavedChanges(false);
          setGlobalUnsavedChanges(false);
        }}
        ref={navigationRef}
      >
        <div className={isYolo ? 'relative h-full w-full p-3 lg:p-0' : 'relative px-4 pb-6 pt-2'}>
          {/* Followup Toast Notification */}
          {followupToastVisible && (
            <div 
              className="fixed bottom-4 left-4 bg-gray-800 text-white p-3 rounded-lg shadow-lg z-50 flex flex-col"
              style={{ 
                width: '300px',
                animation: 'fadeIn 0.3s, pulse 2s infinite',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)'
              }}
            >
              <div className="flex justify-between items-center mb-2">
                <div className="font-medium flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  New Followups Available
                </div>
                <button 
                  className="text-gray-400 hover:text-white"
                  onClick={() => {
                    if (followupToastTimeoutRef.current) {
                      clearTimeout(followupToastTimeoutRef.current);
                    }
                    setFollowupToastVisible(false);
                  }}
                >
                  ✕
                </button>
              </div>
              
              <div className="text-sm mb-3">
                Please respond to the followup questions in the chat box to continue.
              </div>
              
              <div className="w-full bg-gray-700 h-1 rounded-full mb-3 overflow-hidden">
                <div 
                  className="bg-blue-500 h-full"
                  style={{ width: `${followupProgress}%`, transition: 'width 0.1s linear' }}
                />
              </div>
              
              <button
                onClick={() => {
                  if (followupToastTimeoutRef.current) {
                    clearTimeout(followupToastTimeoutRef.current);
                  }
                  setFollowupToastVisible(false);
                }}
                className="bg-blue-500 hover:bg-blue-600 text-white py-1 px-4 rounded self-end text-sm font-medium transition-colors"
              >
                Got it
              </button>
            </div>
          )}
          
          {/* Success Toast Notification */}
          {successToastVisible && !isYolo && (
            <div 
              className="fixed bottom-4 right-4 bg-gray-800 text-white p-3 rounded-lg shadow-lg z-50 flex flex-col"
              style={{ 
                width: '300px',
                animation: 'fadeIn 0.3s, pulse 2s infinite',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)'
              }}
            >
              <div className="flex justify-between items-center mb-2">
                <div className="font-medium flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Recipe Started
                </div>
                <button 
                  className="text-gray-400 hover:text-white"
                  onClick={() => {
                    if (successToastTimeoutRef.current) {
                      clearTimeout(successToastTimeoutRef.current);
                    }
                    setSuccessToastVisible(false);
                  }}
                >
                  ✕
                </button>
              </div>
              
              <div className="text-sm mb-3">
                {triggerType === 'manual'
                  ? 'Your recipe has started working and will finish when the job is complete.'
                  : triggerType === 'event'
                    ? 'Your recipe is ready and waiting for incoming events.'
                    : 'Your recipe is ready and will work at its scheduled times.'}
              </div>
              
              <div className="w-full bg-gray-700 h-1 rounded-full mb-3 overflow-hidden">
                <div 
                  className="bg-green-500 h-full"
                  style={{ width: `${successProgress}%`, transition: 'width 0.1s linear' }}
                />
              </div>
            </div>
          )}
          {/* Legacy cooking overlay – keep it for standalone Cook actions, but
              suppress it while the consolidated run‑phase overlay is active to
              avoid duplicate spinners. */}
          {/* legacy spinner removed */}
          
          {/* Editor and buttons layout */}
          {generationError && (
            <div className={`mb-3 flex items-center justify-between gap-3 rounded-xl border px-3 py-2 text-sm ${generationError ? 'border-red-200 bg-red-50 text-red-800' : 'border-blue-200 bg-blue-50 text-blue-800'}`}>
              <div className="flex min-w-0 items-center gap-2">
                <span>{generationError}</span>
              </div>
              {generationError && onRetryGeneration && (
                <button type="button" onClick={() => void onRetryGeneration()} className="shrink-0 font-semibold text-blue-700 hover:text-blue-900">
                  Retry
                </button>
              )}
            </div>
          )}
          <div
            className={`flex min-h-0 flex-col gap-3 overflow-y-auto lg:flex-row lg:gap-4 lg:overflow-visible ${isYolo ? 'lg:w-[calc(100%+14rem)]' : ''} ${isYolo && !chefPanelVisible ? '' : 'h-full'}`}
            style={isYolo && !chefPanelVisible && hiddenEditorHeight ? { height: `${hiddenEditorHeight}px` } : undefined}
          >
            {/* Edit box - wider to take up more space */}
            <div className={`min-h-[56dvh] min-w-0 shrink-0 lg:min-h-0 lg:flex-1 lg:shrink ${isYolo ? 'flex flex-col rounded-2xl border border-[#eadfca] bg-white p-2 shadow-[0_12px_32px_rgba(83,63,32,0.08)]' : 'lg:max-w-[90%]'}`}>
              {isYolo && (
                <div className="flex min-w-0 items-center justify-between gap-4 px-2 pb-2 pt-1">
                  <div className="flex min-w-0 items-center gap-2">
                    {onToggleChefPanel ? (
                      <button
                        type="button"
                        onClick={onToggleChefPanel}
                        aria-label={chefPanelVisible ? 'Hide Chef' : 'Show Chef'}
                        aria-expanded={chefPanelVisible}
                        aria-controls="recipe-chef-panel"
                        title={chefPanelVisible ? 'Hide Chef' : 'Show Chef'}
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-800 transition hover:bg-amber-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
                      >
                        <span aria-hidden="true">✦</span>
                      </button>
                    ) : (
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-800" aria-hidden="true">✦</span>
                    )}
                    <span className="truncate text-sm font-semibold text-stone-900" title={effectiveName}>
                      {effectiveName || 'Untitled recipe'}
                    </span>
                  </div>
                  <span className="shrink-0 text-xs font-medium text-stone-500">
                    {lastSaveTime
                      ? `Saved ${lastSaveTime.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`
                      : 'Not saved yet'}
                  </span>
                </div>
              )}
              <div className={isYolo ? 'min-h-0 flex-1 overflow-hidden rounded-xl' : ''}>
                {generationActive ? (
                  <div className={`recipe-generation-stage h-full min-h-[20rem] overflow-hidden rounded-xl bg-gradient-to-b from-amber-50 to-orange-50 ${showGenerationAnimation ? 'recipe-generation-stage--pending' : ''}`}>
                    <div className="recipe-generation-stage__fire">
                      <RecipeFireAnimation />
                    </div>
                    {showGenerationAnimation ? (
                      <div className="recipe-generation-stage__pending-copy px-6 text-center">
                        <div className="mt-2 text-lg font-semibold text-stone-900">Your recipe is being generated…</div>
                        <div className="mt-1 text-sm text-stone-500">Chef is turning your description into clear instructions.</div>
                      </div>
                    ) : (
                      <div className={`recipe-generation-stage__content ${isYolo ? 'h-full min-h-0' : ''}`}>
                        <StreamingSaltShaker active value={content} className={isYolo ? 'h-full min-h-0' : ''}>
                          <MarkupEditor
                            id="content"
                            value={content}
                            onChange={handleContentChange}
                            placeholder="Type your recipe here… For example: Every day, check the weather and tell me if I need an umbrella."
                            fillHeight={isYolo}
                            readOnly
                            focusRequest={editorFocusRequest}
                          />
                        </StreamingSaltShaker>
                      </div>
                    )}
                  </div>
                ) : (
                  <StreamingSaltShaker active={false} value={content} className={isYolo ? 'h-full min-h-0' : ''}>
                    <MarkupEditor
                      id="content"
                      value={content}
                      onChange={handleContentChange}
                      placeholder="Type your recipe here… For example: Every day, check the weather and tell me if I need an umbrella."
                      fillHeight={isYolo}
                      readOnly={false}
                      focusRequest={editorFocusRequest}
                    />
                  </StreamingSaltShaker>
                )}
              </div>
            </div>
            
            {/* Recipe controls and subscription summary on the right */}
            <div ref={controlsColumnRef} className="flex w-full shrink-0 self-start flex-col lg:w-52">
            <div className={`flex w-full flex-col gap-1 overflow-hidden rounded-2xl p-px ${isYolo ? 'bg-[#e4d8c4] shadow-[0_12px_32px_rgba(83,63,32,0.08)]' : 'bg-gray-200 shadow-sm'}`}>
              <div className={`overflow-hidden rounded-b-lg rounded-t-[15px] border ${isYolo ? 'border-[#e4d8c4] bg-[#fffdf9]' : 'border-gray-200 bg-white'}`}>
              <section className="p-3">
              <div className="mb-3">
                <div className={`flex items-center gap-2 text-sm font-semibold ${isYolo ? 'text-stone-900' : 'text-gray-900'}`}>
                  <span className={`h-2 w-2 shrink-0 rounded-full ${isActive ? 'bg-emerald-500 ring-4 ring-emerald-500/10' : planSuspendedAt ? 'bg-amber-500 ring-4 ring-amber-500/10' : 'bg-stone-400 ring-4 ring-stone-400/10'}`} aria-hidden="true" />
                  {recipeStateTitle}
                </div>
              </div>
              {isYolo && runPhase !== 'idle' && !executionGuide && (
                <div className="mb-3 flex items-center gap-2 rounded-xl bg-amber-100 px-3 py-2 text-sm font-medium text-amber-900">
                  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle className="opacity-30" cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" />
                    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                  </svg>
                  {runPhase === 'autosave' ? 'Saving changes…' : runPhase === 'stop' ? 'Stopping…' : runPhase === 'apply' ? 'Applying…' : 'Starting…'}
                </div>
              )}
              {isYolo && successToastVisible && (
                <div className="mb-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                  <div className="font-semibold">Recipe started</div>
                  <div className="mt-0.5 text-xs text-emerald-700">Everything is ready.</div>
                  <div className="mt-2 h-1 overflow-hidden rounded-full bg-emerald-100">
                    <div className="h-full bg-emerald-500 transition-[width]" style={{ width: `${successProgress}%` }} />
                  </div>
                </div>
              )}
              <button
                onClick={handleStartRecipe}
                disabled={primaryActionDisabled}
                className={`flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-xl border px-2 py-3 text-sm font-semibold shadow-sm transition disabled:cursor-not-allowed disabled:shadow-none ${isYolo ? 'border-emerald-600 bg-emerald-600 text-white hover:border-emerald-700 hover:bg-emerald-700 disabled:border-stone-200 disabled:bg-stone-200 disabled:text-stone-500' : 'border-indigo-600 bg-indigo-600 text-white hover:border-indigo-700 hover:bg-indigo-700 disabled:border-gray-200 disabled:bg-gray-200 disabled:text-gray-500'}`}
                type="button"
              >
                <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M8 5.5v13l10-6.5-10-6.5Z" />
                </svg>
                {isCheckingStart ? 'One moment…' : isRunning ? (planSuspendedAt ? 'Resuming…' : isActive ? 'Applying…' : 'Starting…') : primaryActionLabel}
              </button>

              <button
                onClick={handleDiscardChanges}
                disabled={!hasUnappliedChanges || isCooking || isRunning || generationActive}
                className={`mt-2 flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-xl border px-2 py-2.5 text-sm font-medium transition disabled:cursor-not-allowed ${isYolo ? 'border-stone-200 bg-white text-stone-700 hover:bg-stone-50 disabled:bg-stone-50 disabled:text-stone-400' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:bg-gray-50 disabled:text-gray-400'}`}
                type="button"
              >
                <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M9 7 4 12l5 5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M20 17a7 7 0 0 0-7-7H4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Discard changes
              </button>

              <div className={`mt-2 grid gap-2 ${onToggleChefPanel ? 'grid-cols-2 lg:block' : 'grid-cols-1'}`}>
                <button
                  onClick={handleToggleActivation}
                  disabled={!isActive || !!isCooking || !!isRunning || !effectiveName}
                  className={`flex w-full items-center justify-center gap-2 rounded-xl border border-red-300 bg-red-50 px-3 py-2.5 font-medium text-red-700 transition hover:border-red-400 hover:bg-red-100 disabled:cursor-not-allowed ${isYolo ? 'disabled:border-stone-200 disabled:bg-stone-50 disabled:text-stone-400' : 'disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-400'}`}
                  type="button"
                >
                  <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <rect x="6" y="6" width="12" height="12" rx="1.5" />
                  </svg>
                  Stop recipe
                </button>
                {onToggleChefPanel && (
                  <button
                    type="button"
                    onClick={onToggleChefPanel}
                    aria-expanded={chefPanelVisible}
                    aria-controls="recipe-chef-panel"
                    className={`flex w-full items-center justify-center gap-2 rounded-xl border px-3 py-2.5 font-medium transition lg:mt-2 ${isYolo ? 'border-amber-300 bg-amber-50 text-amber-900 hover:border-amber-400 hover:bg-amber-100' : 'border-indigo-200 bg-indigo-50 text-indigo-800 hover:border-indigo-300 hover:bg-indigo-100'}`}
                  >
                    <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M12 3l1.2 3.8L17 8l-3.8 1.2L12 13l-1.2-3.8L7 8l3.8-1.2L12 3Z" />
                      <path d="M18.5 13.5l.7 2.3 2.3.7-2.3.7-.7 2.3-.7-2.3-2.3-.7 2.3-.7.7-2.3Z" />
                      <path d="M5 14l.6 1.9 1.9.6-1.9.6L5 19l-.6-1.9-1.9-.6 1.9-.6L5 14Z" />
                    </svg>
                    {chefPanelVisible ? 'Hide Chef' : 'Show Chef'}
                  </button>
                )}
              </div>
              </section>

              <section className={`border-t p-3 ${isYolo ? 'border-[#eee5d7]' : 'border-gray-200'}`} aria-label="Recipe reset and deletion actions">
              <div className="grid grid-cols-2 gap-2 lg:block lg:space-y-2">
              <button
                onClick={() => {
                  setResetError(null);
                  setShowResetDialog(true);
                }}
                disabled={!hasStarted || !!isCooking || !!isRunning || !effectiveName}
                className="flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2.5 font-medium text-gray-900 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
                type="button"
              >
                <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M4 7v5h5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M5.5 16a8 8 0 1 0-.6-7.2L4 12" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Reset recipe
              </button>
              <button
                onClick={handleDelete}
                disabled={isCooking || isRunning}
                className="group flex min-h-11 w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-xl border border-gray-200 bg-white px-2 py-2.5 text-sm font-medium text-gray-900 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
                type="button"
              >
                <svg className="h-4 w-4 shrink-0 text-red-600 group-disabled:text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M4 7h16M9 7V4h6v3M7 7l1 13h8l1-13M10 11v5M14 11v5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Delete recipe
              </button>
              </div>
              </section>
              </div>

              <section className={`rounded-b-[15px] rounded-t-lg border p-3 ${isYolo ? 'border-[#e4d8c4] bg-[#fbf7ee]' : 'border-gray-200 bg-gray-50'}`} aria-labelledby="recipe-subscription-title">
              <div>
                <span className={`text-[10px] font-semibold uppercase tracking-wide ${isYolo ? 'text-stone-500' : 'text-gray-500'}`}>Subscription</span>
                <h2 id="recipe-subscription-title" className={`mt-0.5 break-words text-sm font-semibold ${isYolo ? 'text-stone-900' : 'text-gray-900'}`}>
                  {billingSummary?.plan?.name ?? 'Current plan'}
                </h2>
              </div>

              {billingSummary ? (
                <>
                  <div className={`mt-3 text-xs ${isYolo ? 'text-stone-600' : 'text-gray-600'}`}>
                    <span className="block">Run frequency</span>
                    <strong className={`mt-0.5 block whitespace-nowrap ${isYolo ? 'text-stone-900' : 'text-gray-900'}`}>
                      {formatRunFrequency(billingSummary.plan?.minimumAutomaticIntervalMinutes)}
                    </strong>
                  </div>
                  <div className={`mt-3 text-xs ${isYolo ? 'text-stone-600' : 'text-gray-600'}`}>
                    <span className="block">Active recipes</span>
                    <strong className={`mt-0.5 block whitespace-nowrap ${isYolo ? 'text-stone-900' : 'text-gray-900'}`}>
                      {automaticRecipesUsed} / {automaticRecipesIncluded ?? 'Unlimited'}
                    </strong>
                  </div>
                  {typeof automaticRecipesIncluded === 'number' && automaticRecipesIncluded > 0 && (
                    <div className={`mt-2 h-1.5 overflow-hidden rounded-full ${isYolo ? 'bg-stone-200' : 'bg-gray-200'}`} aria-label={`${automaticRecipesUsed} of ${automaticRecipesIncluded} active recipes used`}>
                      <div
                        className={`h-full rounded-full ${automaticRecipesUsagePercent >= 100 ? 'bg-red-500' : automaticRecipesUsagePercent >= 75 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                        style={{ width: `${automaticRecipesUsagePercent}%` }}
                      />
                    </div>
                  )}
                  <div className={`mt-3 text-xs ${isYolo ? 'text-stone-600' : 'text-gray-600'}`}>
                    <span className="block">Monthly time</span>
                    <strong className={`mt-0.5 block whitespace-nowrap ${isYolo ? 'text-stone-900' : 'text-gray-900'}`}>
                      {monthlyTimeUsedMinutes.toLocaleString()} / {monthlyTimeIncludedMinutes === null ? 'Unlimited' : monthlyTimeIncludedMinutes.toLocaleString()} min
                    </strong>
                  </div>
                  {monthlyTimeIncludedMinutes !== null && monthlyTimeIncludedMinutes > 0 && (
                    <div className={`mt-2 h-1.5 overflow-hidden rounded-full ${isYolo ? 'bg-stone-200' : 'bg-gray-200'}`} aria-label={`${monthlyTimeUsedMinutes} of ${monthlyTimeIncludedMinutes} monthly minutes used`}>
                      <div
                        className={`h-full rounded-full ${monthlyTimeUsagePercent >= 100 ? 'bg-red-500' : monthlyTimeUsagePercent >= 75 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                        style={{ width: `${monthlyTimeUsagePercent}%` }}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className={`mt-3 h-8 animate-pulse rounded-lg ${isYolo ? 'bg-stone-200' : 'bg-gray-100'}`} aria-label="Loading subscription" />
              )}

              <button
                type="button"
                onClick={() => navigate('/account')}
                className={`mt-3 flex w-full items-center justify-between text-left text-xs font-semibold ${isYolo ? 'text-amber-800 hover:text-amber-950' : 'text-indigo-700 hover:text-indigo-900'}`}
              >
                Manage subscription
                <span aria-hidden="true">→</span>
              </button>
              </section>
            </div>
            </div>
          </div>
        </div>
      </NavigationBlocker>
    </>
  );
};

export default RecipeEditor;
